import React, { useEffect, useState } from "react";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/shadcn/Popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
} from "~/components/shadcn/Command";
import { RxCheck } from "react-icons/rx";
import { Button } from "~/components/v3/themed/Button";
import { If } from "~/components/v3/Conditional";
import { cn } from "~/utils/fontsConfig";
import { HiDotsHorizontal } from "react-icons/hi";
import { PopoverPortal } from "@radix-ui/react-popover";

type OptionType = { value: string; count: number } | string;

const EventTagsPopoverFilter = ({
    placeholder,
    onFilterChange,
    onSelectonChange,
    options,
}: {
    placeholder: string;
    onFilterChange: (value: string) => void;
    onSelectonChange: (tags: string[]) => void;
    options: OptionType[];
}) => {
    const [open, setOpen] = useState(false);
    const [selectedValues, setSelectedValues] = useState<OptionType[]>([]);
    const [allOptions, setAllOptions] = useState<OptionType[]>([]);

    useEffect(() => {
        const allOptions = new Map();
        options.forEach((item) =>
            allOptions.set(typeof item === "string" ? item : item.value, item),
        );
        selectedValues.forEach((item) =>
            allOptions.set(typeof item === "string" ? item : item.value, item),
        );
        setAllOptions([...allOptions.values()]);
        onSelectonChange(
            selectedValues.map((item) =>
                typeof item === "string" ? item : item.value,
            ),
        );
    }, [options, selectedValues, onSelectonChange]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    role="combobox"
                    aria-expanded={open}
                    variant="ghost"
                    className="flex h-auto min-h-[2.5rem] flex-col items-start rounded-full border border-v3-N-500 p-2 text-xs hover:bg-v3-N-500"
                >
                    <div className="flex w-full items-center">
                        <HiDotsHorizontal className="mr-1 h-4 w-4 flex-shrink-0" />
                    </div>
                </Button>
            </PopoverTrigger>
            <PopoverPortal>
                <PopoverContent
                    className="w-[330px] bg-primary p-0 md:w-[400px]"
                    align="start"
                >
                    <Command shouldFilter={false}>
                        <CommandInput
                            placeholder={placeholder}
                            onValueChange={onFilterChange}
                        />
                        <CommandEmpty>No event tags found.</CommandEmpty>
                        <CommandGroup className="max-h-[300px] overflow-y-auto">
                            {allOptions.map((option, index) => {
                                const isString = typeof option === "string";
                                const value = isString ? option : option.value;
                                const isSelected =
                                    selectedValues.includes(option);
                                return (
                                    <CommandItem
                                        key={index}
                                        onSelect={() => {
                                            setSelectedValues((vals) => {
                                                if (vals.includes(option))
                                                    return vals.filter(
                                                        (e) => e !== option,
                                                    );
                                                return [...vals, option];
                                            });
                                        }}
                                    >
                                        <div
                                            className={cn(
                                                "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                                isSelected
                                                    ? "bg-primary text-v3-N-300"
                                                    : "opacity-50 [&_svg]:invisible",
                                            )}
                                        >
                                            <RxCheck
                                                className={cn("h-4 w-4")}
                                            />
                                        </div>
                                        <span>{value}</span>
                                        <If condition={!isString}>
                                            <span className="ml-auto flex h-4 w-4 items-center justify-center font-mono text-xs">
                                                {!isString && option.count}
                                            </span>
                                        </If>
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </Command>
                </PopoverContent>
            </PopoverPortal>
        </Popover>
    );
};

export default EventTagsPopoverFilter;
